import React, { useEffect, useState, useRef } from "react";
import { TextField, PrimaryButton, ActionButton, Icon, DefaultButton, Link, IconButton, TooltipHost, Checkbox, Dialog, DialogType, DialogFooter, DatePicker, ComboBox, IComboBoxOption, Pivot, PivotItem } from "@fluentui/react";
import DataTable from 'react-data-table-component';
import { userAPI } from "../../UserAPI";
import { useAtom } from "jotai";
import { authenticationAtom } from "../../State";
import { from, MarketplaceOffer, PromptTool } from "@the-real-insight/tri-model";
import { useModalContext } from "../../components/ModalProvider";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useTestDialog } from "../hooks/useTestDialog";
import { OfferStatus } from "@the-real-insight/tri-model";
import { useSearchParams, useNavigate } from "react-router-dom";
import truncate from "lodash.truncate";
import OfferStatusManagement from '../OfferStatusManagement';
import MarketingDetails from '../MarketingDetails';
import SampleRequests from '../SampleRequests';
import PlanPanel from '../PlanPanel';
import ZodEditor from './ZodEditor';
import { z } from 'zod';

// Import ZodField type from ZodEditor
interface ZodField {
    id: string;
    name: string;
    type: 'string' | 'number' | 'boolean' | 'array' | 'object' | 'date' | 'email' | 'url';
    required: boolean;
    description?: string;
    defaultValue?: string;
    children?: ZodField[];
    isArray?: boolean;
    arrayType?: 'string' | 'number' | 'boolean' | 'object';
}

export default function PromptToolManagement() {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError, showDeleteDialog } = useModalContext() as any;
    const { TestDialog, openTestDialog, closeTestDialog } = useTestDialog();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [promptTools, setPromptTools] = useState<any[]>([]);
    const [selectedPromptTool, setSelectedPromptTool] = useState<any>();
    const [loadingPromptTools, setLoadingPromptTools] = useState(false);
    const [linkDialogOpen, setLinkDialogOpen] = useState<boolean>(false);
    const [linkUrl, setLinkUrl] = useState<string>('');
    const isUpdating = useRef(false);
    const initialKey = searchParams.get('key') || 'general';
    const [selectedKey, setSelectedKey] = useState<any>(initialKey);

    const onRenderItemStyles = {
        root: { padding: '10px' },
    };

    const promptToolColumns = [{
        name: '',
        width: '150px',
        cell: (promptTool: any) => <div className='flexGrow1 displayFlex justifyContentCenter'>
            <TooltipHost content="Bearbeiten">
                <ActionButton iconProps={{ iconName: 'Edit' }} onClick={() => updateSelectedPromptTool(promptTool)}></ActionButton>
            </TooltipHost>
            <TooltipHost content="Testen">
                <ActionButton iconProps={{ iconName: 'Rocket' }} onClick={() => openTestDialog(promptTool)}></ActionButton>
            </TooltipHost>
            <TooltipHost content="Löschen">
                <ActionButton iconProps={{ iconName: 'Delete' }} styles={{
                    root: { color: '#A4262C' },
                    icon: { color: '#A4262C' },
                    label: { color: '#A4262C' }
                }} onClick={() => {
                    try {
                        showDeleteDialog(`Prompt Tool ${promptTool.name} löschen`,
                            promptTool.name,
                            async () => {
                                await userAPI.deleteMarketplaceOffer(authentication.customer, promptTool);
                                setPromptTools(promptTools.filter((pt: any) => pt._id !== promptTool._id));
                            });
                    } catch (error) {
                        pushError(error);
                    }
                }}></ActionButton>
            </TooltipHost>
        </div>
    }, {
        width: '200px',
        name: 'Name',
        cell: (promptTool: any) => <div>{promptTool.name}</div>
    }, {
        width: '200px',
        name: 'Intern verfügbar',
        cell: (promptTool: any) => <div className='flexGrow1 displayFlex justifyContentCenter'>{promptTool.published ?
            <div><Icon iconName='CheckMark' style={{ color: 'green' }} /></div>
            :
            <div><Icon iconName='Cancel' style={{ color: 'red' }} /></div>
        }</div>
    }, {
        width: '200px',
        name: 'Im Marktplatz angeboten',
        cell: (promptTool: any) => <div className='flexGrow1 displayFlex justifyContentCenter'>{promptTool.offered ?
            <div><Icon iconName='CheckMark' style={{ color: 'green' }} /></div>
            :
            <div><Icon iconName='Cancel' style={{ color: 'red' }} /></div>
        }</div>
    }, {
        width: '400px',
        name: 'Beschreibung',
        cell: (promptTool: any) => <div>{truncate(promptTool.synopsis, { length: 200, separator: /,? +/ })}</div>
    }];

    const linkColumns = [{
        name: '',
        width: '80px',
        cell: (link: any) => <div className='flexGrow1 displayFlex justifyContentCenter'>
            <ActionButton iconProps={{ iconName: 'Delete' }} styles={{
                root: { color: '#A4262C' },
                icon: { color: '#A4262C' },
                label: { color: '#A4262C' }
            }} onClick={() => {
                const updatedLinks = selectedPromptTool.links.filter((l: any) => l._id !== link._id);
                setSelectedPromptTool({ ...selectedPromptTool, links: updatedLinks });
            }}></ActionButton>
        </div>
    }, {
        key: "url",
        name: "URL",
        fieldName: "url",
        width: "400px",
        cell: (row: any) => <div>{row.url}</div>
    }, {
        key: "description",
        name: "Beschreibung",
        fieldName: "description",
        width: "300px",
        cell: (row: any) => <div>{row.description}</div>
    }];

    const loadPromptTools = async () => {
        try {
            setLoadingPromptTools(true);
            const promptTools = await userAPI.query(from(PromptTool.type));

            setPromptTools(promptTools);
        } catch (error) {
            pushError(error);
        } finally {
            setLoadingPromptTools(false);
        }
    };

    const updateSelectedPromptTool = (promptTool: any) => {
        console.log('=== LOADING PROMPT TOOL ===');
        console.log('promptTool >>>', promptTool);
        console.log('promptTool.inputSchema >>>', promptTool.inputSchema);
        
        // Create a PromptTool instance to use library methods
        const promptToolInstance = new PromptTool(promptTool);
        
        // Use library functions to get field arrays
        try {
            promptTool.inputSchemaFields = promptToolInstance.getInputSchemaFields();
            promptTool.outputSchemaFields = promptToolInstance.getOutputSchemaFields();
            
            console.log('Unmarshaled inputSchemaFields >>>', JSON.stringify(promptTool.inputSchemaFields, null, 2));
            console.log('Unmarshaled outputSchemaFields >>>', JSON.stringify(promptTool.outputSchemaFields, null, 2));
        } catch (error) {
            console.error('Error getting schema fields from library:', error);
            promptTool.inputSchemaFields = [];
            promptTool.outputSchemaFields = [];
        }
        
        setSelectedPromptTool(promptTool);
        setSelectedKey('general');
        searchParams.set('key', 'general');
        setSearchParams(searchParams, { replace: true });
    };

    const createNewPromptTool = () => {
        const newPromptTool = {
            name: '',
            description: '',
            synopsis: '',
            prompt: '',
            sampleRequests: [],
            links: [],
            published: false,
            _type: 'PromptTool'
        };
        setSelectedPromptTool(newPromptTool);
        setSelectedKey('general');
        searchParams.set('key', 'general');
        setSearchParams(searchParams, { replace: true });
    };

    const menuItems = [
        {
            key: 'general',
            icon: 'Global',
            name: 'Allgemeine Informationen',
            title: 'Add',
            onClick: () => setSelectedKey('general'),
        },
        {
            key: 'prompt',
            icon: 'Code',
            name: 'Prompt',
            title: 'Prompt',
            onClick: () => setSelectedKey('prompt'),
        },
        {
            key: 'inputOutput',
            icon: 'DataConnectionLibrary',
            name: 'Eingabe- und Ausgabedaten',
            title: 'Eingabe- und Ausgabedaten',
            onClick: () => setSelectedKey('inputOutput'),
        },
        {
            key: 'sampleRequests',
            icon: 'SampleRequests',
            name: 'Beispielanfragen',
            title: 'Beispielanfragen',
            onClick: () => setSelectedKey('sampleRequests'),
        },
        {
            key: 'details',
            icon: 'Details',
            name: 'Angebotsdetails',
            title: 'Angebotsdetails',
            onClick: () => setSelectedKey('details'),
        },
        {
            key: 'billing',
            icon: 'Billing',
            name: 'Abrechnung',
            title: 'Abrechnung',
            onClick: () => setSelectedKey('billing'),
        }
    ];

    const onImagesChange = async (imageList: any, addUpdateIndex: any) => {
        try {
            const form = new FormData();
            form.append("file", imageList[0].file);
            const image = await userAPI.uploadFile(form);
            setSelectedPromptTool({ ...selectedPromptTool, images: [...(selectedPromptTool.images || []), image.path] });
        } catch (error: any) {
            pushError(error);
        }
    };





    useEffect(() => {
        loadPromptTools();
    }, [authentication.customer]);

    useEffect(() => {
        const keyParam = searchParams.get('key') || 'general';
        if (keyParam !== selectedKey) {
            setSelectedKey(keyParam);
        }
    }, [searchParams]);

    useEffect(() => {
        if (selectedKey) {
            searchParams.set('key', selectedKey);
            setSearchParams(searchParams, { replace: true });
        }
    }, [selectedKey]);

    if (loadingPromptTools) {
        return <LoadingIndicator loading={true} />;
    }

    return <><div>
        {selectedPromptTool
            ?
            <div>
                <div className="displayFlex alignItemsCenter">
                    <div>
                        <div className="textS">Prompt Tool</div>
                        <div className="textHeading">{selectedPromptTool.name}</div>
                    </div>
                    <div className="flexGrow1 displayFlex justifyContentEnd gapM">
                        <DefaultButton className="marginTopM" onClick={() => {
                            setSelectedPromptTool(null);
                        }}>Abbrechen</DefaultButton>
                        {selectedPromptTool._id && selectedPromptTool.lastStatus !== OfferStatus.discontinued ?
                            <PrimaryButton className="marginTopM" onClick={async () => {
                                console.log('Saving prompt tool with fields:', {
                                    inputSchemaFields: selectedPromptTool.inputSchemaFields,
                                    outputSchemaFields: selectedPromptTool.outputSchemaFields
                                });
                                console.log('Selected prompt tool before save:', selectedPromptTool);
                                
                                // Create a PromptTool instance from the plain object
                                const promptToolInstance = new PromptTool(selectedPromptTool);
                                
                                // Use the library methods to update schemas
                                if (selectedPromptTool.inputSchemaFields) {
                                    promptToolInstance.setInputSchemaFields(selectedPromptTool.inputSchemaFields);
                                }
                                if (selectedPromptTool.outputSchemaFields) {
                                    promptToolInstance.setOutputSchemaFields(selectedPromptTool.outputSchemaFields);
                                }
                                
                                console.log('PromptTool instance after schema updates:', {
                                    inputSchema: promptToolInstance.inputSchema,
                                    outputSchema: promptToolInstance.outputSchema
                                });
                                
                                console.log('Final PromptTool instance being saved:', promptToolInstance);
                                
                                // Use the PromptTool instance directly for saving
                                // This ensures all fields are properly preserved
                                await userAPI.updateMarketplaceOffer(authentication.customer, promptToolInstance);
                                setSelectedPromptTool(null);
                            }}>Speichern</PrimaryButton>
                            :
                            <PrimaryButton className="marginTopM" onClick={async () => {
                                try {
                                    console.log('selectedPromptTool >>>', selectedPromptTool);
                                    
                                    // Create a PromptTool instance from the plain object
                                    const promptToolInstance = new PromptTool(selectedPromptTool);
                                    
                                    // Use the library methods to update schemas
                                    if (selectedPromptTool.inputSchemaFields) {
                                        promptToolInstance.setInputSchemaFields(selectedPromptTool.inputSchemaFields);
                                    }
                                    if (selectedPromptTool.outputSchemaFields) {
                                        promptToolInstance.setOutputSchemaFields(selectedPromptTool.outputSchemaFields);
                                    }
                                    
                                    console.log('PromptTool instance after schema updates (create):', {
                                        inputSchema: promptToolInstance.inputSchema,
                                        outputSchema: promptToolInstance.outputSchema
                                    });
                                    
                                    // Use the PromptTool instance directly for saving
                                    // This ensures all fields are properly preserved
                                    await userAPI.createMarketplaceOffer(authentication.customer, promptToolInstance);
                                    setSelectedPromptTool(null);
                                } catch (error) {
                                    pushError(error);
                                }
                            }}>Erstellen</PrimaryButton>
                        }
                    </div>
                </div>
                <OfferStatusManagement
                    marketplaceOffer={selectedPromptTool}
                    updateSelectedMarketplaceOffer={setSelectedPromptTool}
                />
                <div className="marginTopL width1200 displayFlex gapM">
                    <div style={{ minWidth: '200px', maxWidth: '200px' }}>
                        {menuItems.map((item) => (
                            <div key={item.key} className={`paddingS displayFlex alignItemsCenter hoverHand ${selectedKey === item.key ? 'backgroundColorLightestGrey' : ''}`} onClick={() => setSelectedKey(item.key)}>
                                <div>{item.name}</div>
                            </div>
                        ))}
                    </div>
                    <div>
                        {(() => {
                            switch (selectedKey) {
                                case 'general':
                                    return (
                                        <div className="width600">
                                            <div>
                                                <TextField
                                                    label="Name des Prompt Tools"
                                                    value={selectedPromptTool.name}
                                                    onChange={(e, value) => {
                                                        setSelectedPromptTool({ ...selectedPromptTool, name: value });
                                                    }}
                                                />
                                            </div>
                                            <div className="marginTopS">
                                                <TextField
                                                    label="Beschreibung des Prompt Tools"
                                                    value={selectedPromptTool.synopsis}
                                                    multiline
                                                    rows={15}
                                                    onChange={(e, value) => {
                                                        setSelectedPromptTool({ ...selectedPromptTool, synopsis: value });
                                                    }}
                                                />
                                            </div>
                                            <div className="marginTopS textS colorPrimary">Diese Kurzbeschreibung wird von der AI verwendet, um dein Prompt Tool weiter zu durchsuchen, wenn es zur Frage eines Nutzers passt.</div>
                                        </div>
                                    );
                                case 'prompt':
                                    return (
                                        <div className="width600">
                                            <div>
                                                <TextField
                                                    label="Prompt"
                                                    value={selectedPromptTool.prompt}
                                                    multiline
                                                    rows={20}
                                                    onChange={(e, value) => {
                                                        setSelectedPromptTool({ ...selectedPromptTool, prompt: value });
                                                    }}
                                                    placeholder="Gib hier den Prompt ein, der für dieses Tool verwendet werden soll..."
                                                />
                                            </div>
                                            <div className="marginTopS textS colorPrimary">Dieser Prompt wird von der AI verwendet, um das Tool auszuführen.</div>
                                        </div>
                                    );
                                case 'inputOutput':
                                    return (
                                        <div className="width1000">
                                            <Pivot className="marginTopL">
                                                <PivotItem headerText="Eingabe" itemIcon="DataConnectionLibrary">
                                                    <div className="marginTopM">
                                                        {(() => { console.log('Rendering Eingabe ZodEditor with initialFields:', selectedPromptTool.inputSchemaFields); return null; })()}
                                                        <ZodEditor
                                                            value={selectedPromptTool.inputSchema || ''}
                                                            initialFields={selectedPromptTool.inputSchemaFields || []}
                                                            onChange={(value, fields) => {
                                                                console.log('Input schema changed to:', value);
                                                                setSelectedPromptTool({ 
                                                                    ...selectedPromptTool, 
                                                                    inputSchema: value,
                                                                    inputSchemaFields: fields
                                                                });
                                                            }}
                                                            placeholder="Ziehe Feldtypen hierher, um das Eingabeschema zu definieren..."
                                                        />
                                                    </div>
                                                </PivotItem>
                                                <PivotItem headerText="Ausgabe" itemIcon="DataConnectionLibrary">
                                                    <div className="marginTopM">
                                                        {(() => { console.log('Rendering Ausgabe ZodEditor with initialFields:', selectedPromptTool.outputSchemaFields); return null; })()}
                                                        <ZodEditor
                                                            value={selectedPromptTool.outputSchema || ''}
                                                            initialFields={selectedPromptTool.outputSchemaFields || []}
                                                            onChange={(value, fields) => {
                                                                console.log('Output schema changed to:', value);
                                                                setSelectedPromptTool({ 
                                                                    ...selectedPromptTool, 
                                                                    outputSchema: value,
                                                                    outputSchemaFields: fields
                                                                });
                                                            }}
                                                            placeholder="Ziehe Feldtypen hierher, um das Ausgabeschema zu definieren..."
                                                        />
                                                    </div>
                                                </PivotItem>
                                            </Pivot>
                                            <div className="marginTopS textS colorPrimary">Diese Zod Schemas helfen der AI zu verstehen, welche Daten das Tool erwartet und zurückgibt.</div>
                                        </div>
                                    );                                
                                case 'sampleRequests':
                                    return (
                                        <SampleRequests
                                            type="PromptTool"
                                            marketplaceOffer={selectedPromptTool as MarketplaceOffer}
                                            setMarketplaceOffer={setSelectedPromptTool}
                                        />
                                    );
                                case 'details':
                                    return (
                                        <MarketingDetails
                                            type="PromptTool"
                                            marketplaceOffer={selectedPromptTool as MarketplaceOffer}
                                            setMarketplaceOffer={setSelectedPromptTool}
                                            onImagesChange={onImagesChange}
                                        />
                                    );
                                case 'billing':
                                    return (
                                        <PlanPanel
                                            type="PromptTool"
                                            marketplaceOffer={selectedPromptTool as MarketplaceOffer}
                                            setMarketplaceOffer={setSelectedPromptTool}
                                        />
                                    );
                                default:
                                    return <div>Unbekannter Menüpunkt</div>;
                            }
                        })()}
                    </div>
                </div>
            </div>
            :
            <div className='marginTopM'>
                <ActionButton
                    iconProps={{ iconName: 'Add' }}
                    onClick={createNewPromptTool}
                >
                    Prompt Tool hinzufügen
                </ActionButton>

                <DataTable
                    title="Prompt Tools"
                    columns={promptToolColumns}
                    data={promptTools}
                    pagination
                    responsive
                    highlightOnHover
                    pointerOnHover
                />
            </div>
        }

        <Dialog
            hidden={!linkDialogOpen}
            onDismiss={() => setLinkDialogOpen(false)}
            maxWidth={600}
            minWidth={600}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Link hinzufügen',
            }}
            modalProps={{
                isBlocking: true,
            }}
        >
            <div className="marginTopM">
                <TextField
                    label="URL"
                    value={linkUrl}
                    onChange={(_, value) => setLinkUrl(value || '')}
                    required
                />
                <TextField
                    label="Beschreibung"
                    multiline
                    rows={3}
                    onChange={(_, value) => setLinkUrl(value || '')}
                />
            </div>
            <DialogFooter className="marginTopXXL">
                <DefaultButton onClick={() => setLinkDialogOpen(false)} text="Abbrechen" />
                <PrimaryButton onClick={() => {
                    const newLink = {
                        _id: Date.now().toString(),
                        url: linkUrl,
                        description: linkUrl
                    };
                    setSelectedPromptTool({
                        ...selectedPromptTool,
                        links: [...(selectedPromptTool.links || []), newLink]
                    });
                    setLinkUrl('');
                    setLinkDialogOpen(false);
                }} text="Hinzufügen" />
            </DialogFooter>
        </Dialog>

        <TestDialog />
    </div></>;
} 